import { debounce } from '@/utils/debounce'
import { nextTick } from 'vue'
import type { Directive, DirectiveBinding } from 'vue'

/**
 * 加载更多指令配置选项
 */
export interface LoadMoreOptions {
  /** 距离底部的阈值（像素），默认 3 */
  threshold?: number
  /** 防抖延迟（毫秒），默认 100 */
  delay?: number
  /** 是否禁用，默认 false */
  disabled?: boolean
  /** 是否正在加载，默认 false */
  loading?: boolean
  /** 是否还有更多数据，默认 true */
  hasMore?: boolean
}

/**
 * 存储每个元素的事件监听器和状态
 */
const elementMap = new WeakMap<HTMLElement, {
  handlers: Array<{ element: HTMLElement, handler: (e: Event) => void }>
  lastScrollTop: number
  options: LoadMoreOptions
}>()

/**
 * 处理滚动事件
 */
function createScrollHandler (
  scroller: HTMLElement,
  callback: () => void,
  getOptions: () => LoadMoreOptions
) {
  let lastScrollTop = 0

  return debounce((e: Event) => {
    const options = getOptions()
    if (!options) {
      return
    }
    
    const threshold = options.threshold ?? 3
    const delay = options.delay ?? 100
    
    const currentScrollTop = scroller.scrollTop
    
    // 如果滚动位置没有变化，直接返回
    if (currentScrollTop === lastScrollTop) {
      return
    }
    
    lastScrollTop = currentScrollTop
    
    // 检查是否禁用或正在加载
    if (options.disabled === true || options.loading === true) {
      return
    }
    
    // 检查是否还有更多数据
    if (options.hasMore === false) {
      return
    }
    
    // 计算距离底部的距离
    const scrollHeight = scroller.scrollHeight
    const clientHeight = scroller.clientHeight
    const scrollTop = scroller.scrollTop
    const distanceToBottom = scrollHeight - clientHeight - scrollTop

    // 无纵向滚动条时 distanceToBottom 常为 0，会被误判为「触底」；仅在内容可滚动时再按阈值触发
    if (scrollHeight <= clientHeight) {
      return
    }

    // 如果距离底部小于等于阈值，触发加载更多
    if (distanceToBottom <= threshold) {
      callback()
    }
  }, getOptions().delay ?? 100)
}

/**
 * 为元素绑定滚动监听
 */
function bindScrollListener (
  el: HTMLElement,
  binding: DirectiveBinding
) {
  // 如果 binding.value 是 undefined 或 null，直接返回
  if (!binding.value) {
    return
  }
  
  // 获取配置和回调函数
  let callback: () => void
  let options: LoadMoreOptions = {}
  
  if (typeof binding.value === 'function') {
    callback = binding.value
  } else if (typeof binding.value === 'object') {
    callback = binding.value.callback || (() => {})
    options = {
      threshold: binding.value.threshold,
      delay: binding.value.delay,
      disabled: binding.value.disabled,
      loading: binding.value.loading,
      hasMore: binding.value.hasMore
    }
  } else {
    return
  }
  
  // 获取或创建元素状态
  let elementState = elementMap.get(el)
  if (!elementState) {
    elementState = {
      handlers: [],
      lastScrollTop: 0,
      options: {}
    }
    elementMap.set(el, elementState)
  }
  
  // 更新选项
  elementState.options = { ...elementState.options, ...options }
  
  // 清理旧的事件监听器
  elementState.handlers.forEach(({ element, handler }) => {
    element.removeEventListener('scroll', handler)
  })
  elementState.handlers = []
  
  // 处理 el-table
  if (el.classList.contains('el-table')) {
    nextTick(() => {
      const tableBodyWrappers = el.querySelectorAll('.el-table__body-wrapper')
      tableBodyWrappers.forEach((dom: Element) => {
        const handler = createScrollHandler(
          dom as HTMLElement,
          callback,
          () => elementState!.options
        )
        dom.addEventListener('scroll', handler)
        elementState!.handlers.push({
          element: dom as HTMLElement,
          handler
        })
      })
    })
  } else {
    // 处理自定义元素
    nextTick(() => {
      const handler = createScrollHandler(el, callback, () => elementState!.options)
      el.addEventListener('scroll', handler)
      elementState!.handlers.push({
        element: el,
        handler
      })
    })
  }
}

/**
 * 清理元素的事件监听器
 */
function unbindScrollListener (el: HTMLElement) {
  const elementState = elementMap.get(el)
  if (elementState) {
    elementState.handlers.forEach(({ element, handler }) => {
      element.removeEventListener('scroll', handler)
    })
    elementState.handlers = []
    elementMap.delete(el)
  }
}

/**
 * 加载更多指令
 * 
 * @example
 * // 基础用法
 * <div v-load-more="handleLoadMore">...</div>
 * 
 * @example
 * // 带配置的用法
 * <div v-load-more="{ 
 *   callback: handleLoadMore, 
 *   threshold: 10, 
 *   delay: 200,
 *   disabled: false,
 *   loading: isLoading,
 *   hasMore: hasMoreData
 * }">...</div>
 * 
 * @example
 * // 在 el-table 上使用
 * <el-table v-load-more="handleLoadMore">...</el-table>
 */
export const loadMore: Directive = {
  mounted (el: HTMLElement, binding: DirectiveBinding) {
    bindScrollListener(el, binding)
  },
  
  updated (el: HTMLElement, binding: DirectiveBinding) {
    // 更新时重新绑定，以支持动态配置
    bindScrollListener(el, binding)
  },
  
  unmounted (el: HTMLElement) {
    // 卸载时清理事件监听器
    unbindScrollListener(el)
  }
}
